// SPDX-FileCopyrightText: 2026 Mads Ringblom
//
// SPDX-License-Identifier: GPL-3.0-or-later

/**
 * What the Miljø mode's strip card and inspector say about a sample, beyond
 * `miljoe/model.ts`: the rooms it was taken in (from its linked types'
 * parts and its single linked parts — a sample has no room of its own), what
 * it is linked to (whole types, then single parts — frontend-r22.3),
 * the "Kæde" timeline and the next-step button's wording.
 *
 * Pure: no React, no DOM.
 */

import { parseServerUtc, type Sample, type SampleStage, type SurveyPart, type SurveyType } from '../../../api/types';
import { relativeDa } from '../../../bygning/overblik';
import { RESULT_LABEL } from '../../../kortlaegning/vocab';
import { chainSteps, linkedParts, type SampleLinks, type StepState } from '../../../miljoe/model';

/** The linked types that exist, in link order. */
export function linkedOf(typeIds: readonly number[], types: readonly SurveyType[]): SurveyType[] {
  const byId = new Map(types.map((t) => [t.id, t]));
  return typeIds.flatMap((id) => {
    const t = byId.get(id);
    return t ? [t] : [];
  });
}

/** A sample's links as `linksOf` gives them; an older server sends no `part_codes`. */
type Links = Pick<SampleLinks, 'type_ids'> & { part_codes?: readonly string[] };

function roomOf(p: Pick<SurveyPart, 'room_name' | 'room_id'>): string {
  return p.room_name.trim() || (p.room_id !== null ? `Rum ${p.room_id}` : '');
}

/**
 * The rooms the sample covers, distinct, in order of first appearance: the
 * parts of its linked (non-rejected) types, then its single linked parts.
 * Parts outside any room are skipped.
 */
export function sampleRooms(links: Links, types: readonly SurveyType[]): string[] {
  const seen = new Set<string>();
  const out: string[] = [];
  const add = (p: SurveyPart) => {
    const name = roomOf(p);
    if (!name || seen.has(name.toLowerCase())) return;
    seen.add(name.toLowerCase());
    out.push(name);
  };
  for (const t of linkedOf(links.type_ids, types)) {
    if (t.review_status === 'rejected') continue;
    t.parts.forEach(add);
  }
  for (const { part, type } of linkedParts(links.part_codes ?? [], [...types])) {
    if (type.review_status !== 'rejected') add(part);
  }
  return out;
}

export interface LinkedItem {
  key: string;
  /** The type it opens in Ressourcer (a part's: the type it is filed under). */
  type: SurveyType;
  /** A single part link's code, else null (a whole type). */
  partCode: string | null;
  /** "Vinduespartier", or "RX-008 · Office Zone (Vinduespartier)". */
  label: string;
}

/** What a sample is linked to, as main's "Koblet:" line: whole types first, then single parts with their type. */
export function linkedItems(links: Links, types: readonly SurveyType[]): LinkedItem[] {
  return [
    ...linkedOf(links.type_ids, types).map((t) => ({ key: `t${t.id}`, type: t, partCode: null, label: t.name })),
    ...linkedParts(links.part_codes ?? [], [...types]).map(({ part, type }) => ({
      key: `p${part.code}`,
      type,
      partCode: part.code,
      label: `${part.code}${part.room_name ? ` · ${part.room_name}` : ''} (${type.name})`,
    })),
  ];
}

/** "Klasselokale 1", "Klasselokale 1 +2"; null for none. */
export function firstPlus(names: readonly string[]): string | null {
  if (names.length === 0) return null;
  return names.length === 1 ? names[0] : `${names[0]} +${names.length - 1}`;
}

/** The strip card's link line: the first linked type (or part, as `RX-008 (Type)`), "+N" for more; null when unlinked. */
export function linkedTypeText(links: Links, types: readonly SurveyType[]): string | null {
  return firstPlus(
    linkedItems(links, types).map((i) => (i.partCode === null ? i.label : `${i.partCode} (${i.type.name})`)),
  );
}

export interface TimelineStep {
  stage: SampleStage;
  label: string;
  /** A `svar` sample with a result is finished: its last step counts as done. */
  state: StepState;
  /** The small line under the label. */
  note: string;
}

/**
 * The "Kæde" timeline (prototype v8): the four stages, done ones "udført",
 * the current one with when the sample last changed, the next one "næste
 * skridt", the rest "–". The server keeps no date per stage, so only the
 * current step has one.
 */
export function timeline(sample: Pick<Sample, 'stage' | 'result' | 'updated_at'>, now: Date = new Date()): TimelineStep[] {
  const steps = chainSteps(sample);
  const at = steps.findIndex((s) => s.state === 'current');
  const changed = parseServerUtc(sample.updated_at);
  return steps.map((s, i): TimelineStep => {
    if (s.state === 'done') return { ...s, note: 'udført' };
    if (s.state === 'current') {
      if (s.stage === 'svar' && sample.result) {
        const when = changed ? ` · ${relativeDa(changed, now)}` : '';
        return { ...s, state: 'done', note: `${RESULT_LABEL[sample.result]}${when}` };
      }
      return { ...s, note: changed ? relativeDa(changed, now) : 'nu' };
    }
    return { ...s, note: i === at + 1 ? 'næste skridt' : '–' };
  });
}

/** The primary button before the lab has the sample (`advancePatch`); null after. */
export function advanceLabel(stage: SampleStage): string | null {
  if (stage === 'planlagt') return 'Registrér udtag';
  if (stage === 'udtaget') return 'Send til lab';
  return null;
}
